import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArchiveRestore, CheckCircle2, ExternalLink, Loader2, Mail, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useToast } from '../../components/Toast';
import { datumZeit, ladeGeloeschte, mailWiederherstellen, wannText, type GeloeschteMail } from '../../lib/planung';
import { statusLabel } from '../../lib/format';
import { edgeFunctions } from '../../lib/supabase';
import { useFeedbackStore } from '../../stores/feedbackStore';
import '../../components/Planung/PlanungsChips.css';

type Props = {
  suche: string;
  /** Sportschul-Filter wie in der Liste; „Übergreifend“ = ohne Sportschule */
  schulen: string[];
  uebergreifend: string;
};

/** Nach dem Wiederherstellen: Mail war in Brevo → neu anlegen anbieten. */
type Zurueck = { id: string; status: string; brevo: 'offen' | 'laeuft' | 'angelegt' | null };

function fehlerText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * „Gelöscht“: Mails, die unter Geplante Mails gelöscht wurden, neueste zuerst
 * (mail_planung_geloescht). Wiederherstellen darf, wer die Mail bearbeiten
 * darf; danach steht sie wieder in der Übersicht — ohne Brevo-Kampagne.
 */
export function GeloeschteListe({ suche, schulen, uebergreifend }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const abfrage = useQuery({ queryKey: ['planung-geloescht'], queryFn: ladeGeloeschte });
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [zurueck, setZurueck] = useState<Zurueck[]>([]);

  const liste = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (abfrage.data ?? []).filter((g) => {
      if (schulen.length) {
        const passt = g.sportschulen.some((s) => schulen.includes(s)) || (g.sportschulen.length === 0 && schulen.includes(uebergreifend));
        if (!passt) return false;
      }
      return !q || [g.betreff, g.name].some((t) => t?.toLowerCase().includes(q));
    });
  }, [abfrage.data, suche, schulen, uebergreifend]);

  async function wiederherstellen(g: GeloeschteMail) {
    setArbeitet(g.id);
    try {
      const r = await mailWiederherstellen(g.id);
      setZurueck((z) => [...z.filter((x) => x.id !== g.id), { id: g.id, status: r.status, brevo: r.war_in_brevo ? 'offen' : null }]);
      toast.success(r.war_in_brevo
        ? 'Wiederhergestellt. Die Brevo-Kampagne war mitgelöscht — leg sie neu an, bevor die Mail rausgeht.'
        : 'Wiederhergestellt — die Mail steht wieder in der Übersicht.');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['planung-liste'] }),
        qc.invalidateQueries({ queryKey: ['feedback-alle'] }),
      ]);
      void useFeedbackStore.getState().laden();
    } catch (err) {
      toast.error(`Wiederherstellen ging nicht: ${fehlerText(err)}`);
      void abfrage.refetch();
    } finally {
      setArbeitet(null);
    }
  }

  async function inBrevoAnlegen(id: string) {
    setZurueck((z) => z.map((x) => (x.id === id ? { ...x, brevo: 'laeuft' } : x)));
    try {
      await edgeFunctions.createCampaignInBrevo(id);
      setZurueck((z) => z.map((x) => (x.id === id ? { ...x, brevo: 'angelegt' } : x)));
      toast.success('In Brevo als Entwurf angelegt — mit Zielgruppen und interner Kopie.');
      void qc.invalidateQueries({ queryKey: ['planung-liste'] });
    } catch (err) {
      setZurueck((z) => z.map((x) => (x.id === id ? { ...x, brevo: 'offen' } : x)));
      toast.error(`In Brevo anlegen ging nicht: ${fehlerText(err)}`);
    }
  }

  // Wiederhergestellte bleiben stehen (Liste wird erst beim nächsten Öffnen neu geladen),
  // damit der Hinweis „In Brevo neu anlegen“ sichtbar bleibt.
  const alle = abfrage.data ?? [];
  const sichtbar = liste;

  if (abfrage.isLoading) return <div className="gm-laden"><div className="loading-spinner" /></div>;
  if (abfrage.isError) {
    return (
      <div className="alert alert-danger" role="alert">
        <span>Die gelöschten Mails ließen sich nicht laden: {fehlerText(abfrage.error)}</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void abfrage.refetch()}><RefreshCw size={14} aria-hidden="true" /> Nochmal</button>
      </div>
    );
  }

  return (
    <section className="gm-gl" aria-labelledby="gm-gl-titel">
      <header className="gm-gl-kopf">
        <h2 id="gm-gl-titel" className="gm-gl-titel"><Trash2 size={16} aria-hidden="true" /> Gelöscht</h2>
        <p className="gm-gl-text">
          Gelöschte Mails stehen nicht in der Übersicht und nicht in den Feedback-Links. Wiederherstellen holt sie mit Inhalt,
          Versionen und Rückmeldungen zurück — eine Brevo-Kampagne wird dabei nicht wieder angelegt.
        </p>
      </header>

      {sichtbar.length === 0 ? (
        <div className="gm-leer">
          <Trash2 size={32} aria-hidden="true" />
          <p>{alle.length === 0 ? 'Hier ist nichts — es wurde noch keine Mail gelöscht.' : 'Keine gelöschte Mail passt zu diesem Filter.'}</p>
        </div>
      ) : (
        <ul className="gm-gl-liste">
          {sichtbar.map((g) => {
            const z = zurueck.find((x) => x.id === g.id);
            const titel = g.betreff?.trim() || g.name || 'Ohne Betreff';
            return (
              <li key={g.id} className={`gm-gl-eintrag${z ? ' gm-gl-eintrag--zurueck' : ''}`}>
                <span className="gm-gl-symbol" aria-hidden="true">{z ? <CheckCircle2 size={18} /> : <Trash2 size={18} />}</span>
                <div className="gm-gl-haupt">
                  <h3 className="gm-gl-betreff">{titel}</h3>
                  {g.name && g.name.trim() !== titel && <p className="gm-gl-name">{g.name}</p>}
                  <div className="gm-gl-chips">
                    <span className="pl-chip" title={datumZeit(g.geloescht_am)}>
                      <Trash2 size={13} aria-hidden="true" />
                      <span className="pl-chip-text">Gelöscht {wannText(g.geloescht_am)}{g.geloescht_von_name ? ` von ${g.geloescht_von_name}` : ''}</span>
                    </span>
                    {g.status_vorher && (
                      <span className="pl-chip pl-chip--leer" title="Stand vor dem Löschen">
                        <span className="pl-chip-text">war: {statusLabel(g.status_vorher)}</span>
                      </span>
                    )}
                    {g.war_in_brevo && (
                      <span className="pl-chip pl-chip--warn" title="Die Brevo-Kampagne wurde beim Löschen mitgelöscht">
                        <Mail size={13} aria-hidden="true" />
                        <span className="pl-chip-text">Brevo-Kampagne gelöscht</span>
                      </span>
                    )}
                  </div>
                  <div className="gm-gl-schulen">
                    <span className="pl-schulen">
                      {g.sportschulen.length
                        ? g.sportschulen.map((s) => <span key={s} className="pl-schule">{s}</span>)
                        : <span className="pl-schule pl-schule--alle">Alle Sportschulen</span>}
                    </span>
                  </div>
                  {z && (
                    <div className="gm-gl-zurueck" role="status">
                      <p>
                        Wiederhergestellt als „{statusLabel(z.status)}“.{' '}
                        <Link to={`/geplante-mails/${g.id}`} className="gm-gl-link">Mail öffnen <ExternalLink size={12} aria-hidden="true" /></Link>
                      </p>
                      {z.brevo && z.brevo !== 'angelegt' && (
                        <div className="gm-gl-brevo">
                          <span>In Brevo gibt es sie nicht mehr. Neu anlegen (mit Zielgruppen und interner Kopie) — oder sie entsteht mit der nächsten Testmail.</span>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => void inBrevoAnlegen(g.id)} disabled={z.brevo === 'laeuft'}>
                            {z.brevo === 'laeuft' ? <Loader2 size={14} className="spin" aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />} In Brevo neu anlegen
                          </button>
                        </div>
                      )}
                      {z.brevo === 'angelegt' && <p className="gm-gl-ok">In Brevo als Entwurf angelegt.</p>}
                    </div>
                  )}
                </div>
                <div className="gm-gl-aktionen">
                  {!z && g.darf_wiederherstellen && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => void wiederherstellen(g)} disabled={arbeitet !== null} aria-label={`„${titel}“ wiederherstellen`}>
                      {arbeitet === g.id ? <Loader2 size={14} className="spin" aria-hidden="true" /> : <ArchiveRestore size={14} aria-hidden="true" />}
                      <span>Wiederherstellen</span>
                    </button>
                  )}
                  {!z && !g.darf_wiederherstellen && <span className="gm-gl-hinweis">Wiederherstellen kann, wer die Mail bearbeiten darf.</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
